/**
 * Racine de la nouvelle interface de réservation (docs/ux-v2/SPEC.md § 6.2).
 *
 * Mêmes routes publiques que App.tsx (menus des thèmes, e-mails, retours Alma / 3DS /
 * portefeuille), mêmes effets globaux, mais :
 * - pas de menu interne (.mainAppMenu) : l'en-tête du site le remplace (SPEC EN3) ;
 * - chaque route passe par un module de src/v2/screens (Planning, Class, Account, Prices, Checkout), qui rend
 *   l'écran v1 tel quel tant que son écran v2 n'est pas livré (aucun fichier v1 modifié).
 *
 * Les effets globaux sont repris à l'identique d'App.tsx (fichier v1 non modifiable pour la v2) ;
 * chaque bloc cite sa source. Un seul des deux arbres est monté : jamais de double traitement.
 */
import { Box } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { AlmaPaymentOverlay } from 'components/AlmaPaymentOverlay';
import { useAlmaPaymentProcessor } from 'hooks/useAlmaPaymentProcessor';
import { useUserContext } from 'providers/useUserContext';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { safeLocalStorage } from 'utils/safeStorage';
import { uxV2Source } from './flag';
import { recordPath } from './lib/navHistory';
import { AccountRoutesV2 } from './screens/Account';
import { Charte } from './screens/Charte';
import { CheckoutV2, ScaCallbackV2, WalletCallbackV2 } from './screens/Checkout';
import { ClassConfirmationV2, ClassV2 } from './screens/Class';
import { PlanningV2 } from './screens/Planning';
import { PricesV2 } from './screens/Prices';
import { LandingScreen } from 'screens/Landing';

export function AppV2() {
  const { user } = useUserContext();
  const location = useLocation();
  const { state, processAlmaPayment, cancelPayment } = useAlmaPaymentProcessor();

  // = App.tsx : un même PID Alma n'est traité qu'une fois.
  const processedPaymentIdRef = useRef<string | null>(null);

  // = App.tsx : retour Alma (pid dans le hash réécrit par main.tsx, sinon filet localStorage).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let paymentId = params.get('pid');

    let recoveredFromStorage = false;
    if (!paymentId) {
      const storedPid = safeLocalStorage.getItem('alma_pending_payment');
      const storedTimestamp = safeLocalStorage.getItem('alma_pending_timestamp');

      if (storedPid && storedTimestamp) {
        const age = Date.now() - parseInt(storedTimestamp);
        if (age < 10 * 60 * 1000) {
          paymentId = storedPid;
          recoveredFromStorage = true;
        } else {
          safeLocalStorage.removeItem('alma_pending_payment');
          safeLocalStorage.removeItem('alma_pending_timestamp');
        }
      }
    }

    if (paymentId && processedPaymentIdRef.current !== paymentId) {
      processedPaymentIdRef.current = paymentId;

      if (recoveredFromStorage) {
        Sentry.captureMessage('Alma pid recovered from localStorage fallback', {
          level: 'warning',
          tags: {
            category: 'payment',
            paymentMethod: 'alma',
            alma_step: 'pid_recovered_from_storage',
          },
          extra: { paymentId },
        });
      }

      processAlmaPayment(paymentId);
    }
  }, [location.search, location.pathname, processAlmaPayment]);

  // = App.tsx : utilisateur Sentry.
  useEffect(() => {
    if (user) {
      // Id Mindbody seul : pas d'e-mail dans Sentry (même règle que le back,
      // send_default_pii=false).
      Sentry.setUser({ id: user.Id });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  // = App.tsx : pas de fermeture de page pendant le traitement Alma.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (state.isProcessing) {
        e.preventDefault();
        e.returnValue = 'Votre paiement est en cours de traitement, ne fermez pas cette page.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state.isProcessing]);

  return (
    <Box className="mmv2-app">
      <AlmaPaymentOverlay
        isOpen={state.isProcessing}
        currentStep={state.currentStep}
        currentAttempt={state.currentAttempt}
        maxAttempts={state.maxAttempts}
        isSlowNetwork={state.isSlowNetwork}
        error={state.error}
        errorStep={state.errorStep}
        onCancel={() => {
          if (state.paymentId) {
            cancelPayment(state.paymentId);
          }
        }}
      />

      <ScrollToTop />
      <Routes>
        <Route index Component={PlanningV2} />
        <Route path="studio/:id" Component={PlanningV2} />
        <Route path="studio/:id/week/:weekNumber" Component={PlanningV2} />
        <Route path="coach/:id" Component={PlanningV2} />
        <Route path="cours/:id" Component={PlanningV2} />
        <Route path="studio/:id/cours/:type" Component={PlanningV2} />
        <Route path="tarifs" Component={PricesV2} />
        <Route path="event/:slug" Component={LandingScreen} />
        <Route path="class/:id" Component={ClassV2} />
        <Route path="class/:id/enrollment" Component={ClassV2} />
        <Route path="class/:id/confirmation" Component={ClassConfirmationV2} />
        <Route path="account/*" Component={AccountRoutesV2} />
        <Route path="checkout" Component={CheckoutV2} />
        <Route path="checkout/id/:productId" Component={CheckoutV2} />
        <Route path="checkout/contract/:contractId" Component={CheckoutV2} />
        <Route path="sca-callback" Component={ScaCallbackV2} />
        <Route path="wallet-callback" Component={WalletCallbackV2} />
        {/* Charte des jetons v2 : aperçu administrateur seulement (jamais pour les visiteurs). */}
        {uxV2Source() === 'preview' ? <Route path="v2/charte" Component={Charte} /> : null}
        {/* Route inconnue (ou charte hors aperçu) : le planning plutôt qu'une zone vide. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Box>
  );
}

/**
 * = App.tsx ScrollToTop() : haut de page à chaque changement de route, mais AVANT la peinture et sans animation.
 * Revue du 08/10 : en useEffect, la confirmation de réservation (page plus courte que la fiche) était d'abord peinte à
 * la position de défilement de la fiche, pied de page à l'écran, puis remontait en glissant 0,5 s (`scroll-behavior:
 * smooth` du thème) : décalage de mise en page de 0,93 à 0,98 compté sans saisie récente (la réponse du serveur arrive
 * plusieurs secondes après le tap), CLS « mauvais » à chaque réservation.
 */
function ScrollToTop() {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    // Défilement instantané quel que soit le `scroll-behavior` de la page (le thème le passe en smooth) : style en
    // ligne plutôt que `behavior: 'instant'`, que tous les Safari ne connaissent pas, LU avant le défilement (Chromium
    // ne recalcule pas les styles pour un scrollTo(0, 0) et garderait sinon le smooth du thème).
    const root = document.documentElement;
    const previous = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    void getComputedStyle(root).scrollBehavior;
    window.scrollTo(0, 0);
    root.style.scrollBehavior = previous;
    // Chemin précédent de l'app (« Retour au planning » de la fiche cours, lib/navHistory.ts).
    recordPath(pathname);
  }, [pathname]);

  return null;
}
